<script lang="ts">
  /**
   * YourStrokesCard — one stroke across the swimmer's own 25 m history.
   *
   * The card face is numbers and chart marks only (steering round 3,
   * 2026-09-11; Scott: "graphs and numbers and way less with text"): name with
   * the stroke's color dot and "34 swims"; three tiles Typical 25 / Best 25 /
   * Strokes per 25; the trend tag ("Steady" / "Faster 1.7s", or "4/10 swims"
   * before a verdict) with its "20+ lengths" mark; one bar per swim, oldest →
   * newest. Every sentence (the verdict's rule and evidence, the rest range,
   * what the numbers mean) sits behind ONE "How it's counted" tap at the foot
   * of every card, the glide card's pattern.
   *
   * Bars: the last 5 swims the trend compares in full stroke color, the 5
   * before at half, the rest faint, so the two compared windows show without a
   * sentence naming them. No verdict yet → every bar full.
   *
   * Steering-committee rules (2026-09-01) still hold: no red/green anywhere
   * (the tag is neutral); never a verdict without its evidence (the lengths
   * mark beside the tag, both medians in the tap); "Best 25" is one observed
   * length, and the tile says so.
   */
  import type { StrokeInsightRow } from "../lib/strokeInsights";
  import {
    STROKES_SECTION_EXPLAINER,
    strokeSwimsLabel,
    strokeTrendTag,
    strokeLengthsMark,
    strokeVerdictLine,
    strokeEvidenceLine,
    strokeRestLine,
    strokeBarsSplit,
    STROKES_BARS_LINE,
  } from "../lib/strokeInsights";
  import { fmtSetDate } from "../lib/setHistory";
  import { fmtSeconds1 } from "../lib/format";
  import { strokeColor } from "../lib/designTokens";
  import { openOverlay } from "../lib/overlay";
  import { haptic } from "../lib/haptics";

  interface Props {
    row: StrokeInsightRow;
  }
  let { row }: Props = $props();

  /** The Best 25 tile opens "Fastest 25s · <Stroke>" (Scott: "the best 25 should be clickable"). */
  function openFastest() {
    haptic("selection");
    openOverlay({ kind: "fastest-25s", stroke: row.stroke });
  }

  /** Bars wear this card's own stroke color (VP panel round 1, tabs slice: a
   *  Butterfly card drawn in Freestyle slate read as the wrong stroke). */
  let barFill = $derived(strokeColor(row.stroke));

  let once = $derived(row.swims === 1);
  let showBars = $derived(row.series.length >= 2);
  let maxS = $derived(Math.max(...row.series.map((p) => p.medianS)));
  let tag = $derived(strokeTrendTag(row));
  let lengthsMark = $derived(strokeLengthsMark(row));
  let verdict = $derived(strokeVerdictLine(row));
  let evidence = $derived(strokeEvidenceLine(row));
  let rest = $derived(strokeRestLine(row));
  let split = $derived(strokeBarsSplit(row));

  /** 0.35 is the floor: at 0.22 the faint Freestyle slate bars all but vanished
   *  on the dark card (first render, 2026-09-11), and those are the bars Scott likes. */
  function barOpacity(workoutId: string): number {
    const e = row.evidence;
    if (!split || !e) return 1;
    return e.lastWorkoutIds.includes(workoutId) ? 1 : e.prevWorkoutIds.includes(workoutId) ? 0.6 : 0.35;
  }

  let barsLabel = $derived(
    "Typical 25 per swim, oldest to newest: " + row.series.map((p) => `${fmtSeconds1(p.medianS)}s`).join(", "),
  );
</script>

<article class="stroke-card" aria-label={`${row.stroke}, ${strokeSwimsLabel(row)}`}>
  <header class="stroke-name">
    <span class="t">
      <span class="dot" style:background={strokeColor(row.stroke)} aria-hidden="true"></span>{row.stroke}
    </span>
    <span class="d">{strokeSwimsLabel(row)}</span>
  </header>

  <div class="stat3">
    <div class="tile">
      <div class="n">{fmtSeconds1(row.typical25)}<span class="u">s</span></div>
      <div class="l">Typical 25</div>
      <div class="w">{row.typicalSwims === 1 ? (once ? "this swim" : "last swim") : `last ${row.typicalSwims} swims`}</div>
    </div>
    <!-- "BEST 25 (ONE LENGTH)" does not fit a 375 px tile on one line, so
         the qualifier rides the sub-line: "one length · Sep 30, 2025". With a
         best, the tile is a button (chevron top right) that opens the
         Fastest 25s sheet; spans, not divs, inside a button. -->
    {#if row.best25}
      <button
        type="button"
        class="tile best25"
        aria-label={`Best 25 (one length): ${fmtSeconds1(row.best25.seconds)} seconds, ${fmtSetDate(row.best25.dateMs)}. Show your fastest 25s.`}
        onclick={openFastest}
      >
        <span class="chev" aria-hidden="true">›</span>
        <span class="n">{fmtSeconds1(row.best25.seconds)}<span class="u">s</span></span>
        <span class="l">Best 25</span>
        <span class="w">one length · <span class="nw">{fmtSetDate(row.best25.dateMs)}</span></span>
      </button>
    {:else}
      <div class="tile" aria-label="Best 25 (one length): none yet">
        <div class="n n-empty">—</div>
        <div class="l">Best 25</div>
        <div class="w">one length · none yet</div>
      </div>
    {/if}
    <div class="tile">
      {#if row.typicalSpl !== null}
        <div class="n">{Math.round(row.typicalSpl)}</div>
        <div class="l">Strokes per 25</div>
        <div class="w">{row.typicalSwims === 1 ? (once ? "this swim" : "last swim") : `last ${row.typicalSwims} swims`}</div>
      {:else}
        <div class="n n-empty">—</div>
        <div class="l">Strokes per 25</div>
        <div class="w">no count yet</div>
      {/if}
    </div>
  </div>

  <div class="tag-row">
    <span class="tag" class:pending={row.trend === null}>{tag}</span>
    {#if lengthsMark}<span class="mark">{lengthsMark}</span>{/if}
  </div>

  {#if showBars}
    <div class="spark" role="img" aria-label={barsLabel}>
      {#each row.series as p (p.workoutId + ":" + p.dateMs)}
        <i
          style:background={barFill}
          style:opacity={barOpacity(p.workoutId)}
          style:height={`${Math.max(6, Math.round((p.medianS / maxS) * 100))}%`}
        ></i>
      {/each}
    </div>
  {/if}

  <details class="how">
    <summary>How it's counted</summary>
    <div class="how-body">
      <p>{verdict}</p>
      {#if evidence}<p class="num">{evidence}</p>{/if}
      {#if rest}<p class="num">{rest}</p>{/if}
      {#if split}<p>{STROKES_BARS_LINE}</p>{/if}
      <p>{STROKES_SECTION_EXPLAINER}</p>
    </div>
  </details>
</article>

<style>
  .stroke-card {
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: 14px 16px 6px;
    font-family: var(--font-text);
    color: var(--text-body);
    font-size: 16px;
    line-height: 1.5;
  }
  .stroke-name {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
  }
  .stroke-name .t {
    font-weight: 600;
    font-size: 18px;
    color: var(--text-primary);
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: var(--radius-pill);
    flex-shrink: 0;
  }
  .stroke-name .d {
    font-size: 13px;
    color: var(--text-muted);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  /* Typical / Best / Strokes per 25 */
  .stat3 {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
  /* 6px side padding (YourSetsCard uses 10) and no tracking on the label so
     "STROKES PER 25" stays inside its tile down to 375 px. A wrapped label
     makes the three tiles ragged, which reads as a bug; at 8px + 0.02em it ran
     past the tile edge at 375 (render 2026-09-11). */
  .tile {
    flex: 1;
    min-width: 0;
    background: var(--bg-raised);
    border-radius: var(--radius-button);
    padding: 8px 6px;
  }
  /* The label is 91 px at 11 px; an equal third tile holds 84 at 375 (measured
     with scrollWidth), so the third tile takes a little more of the row rather
     than shrinking the type. */
  .tile:last-child {
    flex: 1.15;
  }
  .n {
    font-size: 22px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
  .n .u {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-body);
    margin-left: 1px;
  }
  .n-empty {
    color: var(--text-muted);
  }
  .l {
    font-size: 11px;
    letter-spacing: 0;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-top: 2px;
    white-space: nowrap;
  }
  .w {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 2px;
  }
  /* A date never breaks in the middle; "one length ·" wraps before it instead. */
  .nw {
    white-space: nowrap;
  }
  /* The Best 25 tile as a button: the same tile, a chevron top right. */
  .best25 {
    position: relative;
    display: block;
    border: 0;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .best25 .n,
  .best25 .l,
  .best25 .w {
    display: block;
  }
  .best25:active {
    background: var(--bg-card);
  }
  .best25:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  .chev {
    position: absolute;
    top: 6px;
    right: 7px;
    font-size: 20px;
    line-height: 1;
    color: var(--text-body);
  }

  /* The trend tag: a neutral pill (never red/green), the lengths mark beside it. */
  .tag-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
  }
  .tag {
    padding: 3px 10px;
    border-radius: var(--radius-pill);
    background: var(--bg-raised);
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .tag.pending {
    color: var(--text-muted);
  }
  .mark {
    font-size: 13px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  /* One bar per swim */
  .spark {
    display: flex;
    align-items: flex-end;
    gap: 3px;
    height: 44px;
    margin-top: 10px;
  }
  .spark i {
    flex: 1;
    border-radius: 2px 2px 0 0;
    display: block;
  }

  /* "How it's counted": the glide card's disclosure, same place on every card,
     a 44 px target. */
  .how {
    margin: 4px 0 0;
  }
  .how summary {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-accent, #B7C7E8);
    cursor: pointer;
    list-style: none;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    -webkit-tap-highlight-color: transparent;
  }
  .how summary::-webkit-details-marker { display: none; }
  .how summary::after {
    content: "›";
    font-size: 18px;
    line-height: 1;
    transition: transform 0.15s ease;
  }
  .how[open] summary::after { transform: rotate(90deg); }
  .how summary:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
  .how-body {
    padding-bottom: 10px;
  }
  .how-body p {
    font-size: 15px;
    color: var(--text-body);
    margin: 0 0 6px;
    line-height: 1.45;
  }
  .how-body .num {
    font-variant-numeric: tabular-nums;
  }
</style>
